import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Editor, Extensions } from "@tiptap/core";
import { Tiptap, useEditor } from "@tiptap/react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep, isEqual } from "lodash";
import { websiteBuilderUpdateElementSettings, websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { TWebsiteBuilderDevice } from "../website/shared/device";
import { WebsiteBuilderRichTextEditorContent } from "./rendering/WebsiteBuilderRichTextEditorContent";
import { WebsiteBuilderRichTextMenu } from "./menu/WebsiteBuilderRichTextMenu";
import { TWebsiteBuilderRichTextType, TWebsiteBuilderRichTextContent, websiteBuilderNormalizeRichTextContent } from "./websiteBuilderRichTextContent";

interface IWebsiteBuilderRichTextBuilderProps {
  id: string;
  type: TWebsiteBuilderRichTextType;
  content: TWebsiteBuilderRichTextContent;
  extensions: Extensions;
  elementBuilderUIPortalId: string;
  sectionId: string;
}

interface IWebsiteBuilderRichTextBuilderInitialState {
  id: string;
  type: TWebsiteBuilderRichTextType;
  content: TWebsiteBuilderRichTextContent;
  activePageId: number;
  deviceView: TWebsiteBuilderDevice;
}

interface IWebsiteBuilderRichTextBuilderCommitDependencies {
  editor: Editor;
  initialState: IWebsiteBuilderRichTextBuilderInitialState;
}

export function WebsiteBuilderRichTextBuilder(props: IWebsiteBuilderRichTextBuilderProps) {
  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const initialState = useMemo<IWebsiteBuilderRichTextBuilderInitialState>(() => ({
    id: props.id,
    type: props.type,
    content: cloneDeep(props.content),
    activePageId,
    deviceView,
  }), []);

  const editor = useEditor({
    content: props.content,
    extensions: props.extensions,
    autofocus: "end",
  });

  const commitDependenciesRef = useRef<IWebsiteBuilderRichTextBuilderCommitDependencies>({ editor, initialState });

  useEffect(() => {
    commitDependenciesRef.current = { editor, initialState };
  }, [editor, initialState]);

  useLayoutEffect(() => {
    return () => {
      const { editor, initialState } = commitDependenciesRef.current;

      if (editor == null) {
        return;
      }

      const content = websiteBuilderNormalizeRichTextContent(editor.getJSON());
      const normalizedInitialContent = editor.schema.nodeFromJSON(initialState.content).toJSON();

      if (isEqual(normalizedInitialContent, content)) {
        return;
      }

      dispatch(
        websiteBuilderUpdateElementSettings(initialState.activePageId, initialState.deviceView, initialState.id, {
          id: initialState.id,
          prevElementSettings: {
            type: initialState.type,
            settings: { content: initialState.content },
          },
          nextElementSettings: {
            type: initialState.type,
            settings: { content },
          },
        }),
      );
    };
  }, []);

  const handleKeydown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();

      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "element",
            editingMode: {
              type: "none",
            },
          },
        }),
      );
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  if (editor == null) {
    return;
  }

  return (
    <Tiptap editor={editor}>
      <WebsiteBuilderRichTextEditorContent />
      <WebsiteBuilderRichTextMenu
        elementId={props.id}
        type={props.type}
        elementBuilderUIPortalId={props.elementBuilderUIPortalId}
        sectionId={props.sectionId}
      />
    </Tiptap>
  );
}
